// src/components/resources/tools/generators/ColorPalette/FormField.tsx
'use client'

import { useCallback } from 'react'
import { clsx } from 'clsx'
import { getString, getNumber, getBoolean, getStringArray } from '~/lib/utils/form-values'
import type { FormFieldProps } from './types'

/**
 * Form field component that renders different input types based on field configuration
 * @param field - The field configuration
 * @param value - Current field value
 * @param onChange - Change handler
 * @param showValidationError - Whether to show validation errors
 * @param isRequired - Whether the field is required
 */
export function FormField({
  field,
  value,
  onChange,
  showValidationError,
}: FormFieldProps): JSX.Element {
  const handleChange = useCallback((newValue: unknown) => {
    onChange(newValue)
  }, [onChange])

  const baseInputClasses = clsx(
    'w-full px-3 py-2 border rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
    showValidationError
      ? 'border-red-300 dark:border-red-600'
      : 'border-gray-300 dark:border-gray-600',
    'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
  )

  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={getString(value)}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          className={baseInputClasses}
        />
      )

    case 'number':
      return (
        <input
          type="number"
          value={getNumber(value) || ''}
          onChange={(e) => handleChange(Number(e.target.value))}
          placeholder={field.placeholder}
          min={field.validation?.min}
          max={field.validation?.max}
          step={0.1}
          className={baseInputClasses}
        />
      )

    case 'select':
      return (
        <select
          value={getString(value)}
          onChange={(e) => handleChange(e.target.value)}
          className={baseInputClasses}
        >
          <option value="">Select an option...</option>
          {field.options?.map((option) => (
            <option key={option.id} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )

    case 'color':
      return (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={getString(value, '#000000')}
            onChange={(e) => handleChange(e.target.value)}
            className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
          />
          <input
            type="text"
            value={getString(value)}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="#000000"
            className={clsx(baseInputClasses, 'flex-1')}
          />
        </div>
      )

    case 'checkbox':
      return (
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={getBoolean(value)}
            onChange={(e) => handleChange(e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {field.label}
          </span>
        </label>
      )

    case 'multiselect': {
      // Use block scope to avoid lexical declaration error
      const selectedValues = getStringArray(value)
      return (
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {field.options?.map((option) => (
            <label key={option.id} className="flex items-center">
              <input
                type="checkbox"
                // Convert option.value to string for comparison since selectedValues is string[]
                checked={selectedValues.includes(String(option.value))}
                onChange={(e) => {
                  const optionValueStr = String(option.value)
                  const newValues = e.target.checked
                    ? [...selectedValues, optionValueStr]
                    : selectedValues.filter(v => v !== optionValueStr)
                  handleChange(newValues)
                }}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </span>
            </label>
          ))}
        </div>
      )
    }

    // Handle all other field types with a default text input
    case 'code':
    case 'html':
    case 'image':
    case 'file':
    case 'url':
    case 'radio':
    case 'markdown':
    case 'email':
    case 'date':
    case 'range':
    case 'ordered':
    case 'visual':
    case 'textarea':
    default:
      return (
        <input
          type="text"
          value={getString(value)}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          className={clsx(
            'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
            showValidationError
              ? 'border-red-300 dark:border-red-600'
              : 'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
          )}
        />
      )
  }
}
